import { useContext, useEffect, useState } from "react";
import { FormLabel, FormSubLabel, FormElement, FormInput, FormToggle, FormRange } from "../../../components/StyledElements";
import { useLiveEvents } from "../../../contexts/LiveEventsContext";
import { getAgents } from "../../../services/clients";
import { getInferenceWorkflows } from "../../../services/workflows";
import { AppContext } from "../../../contexts/AppContext";

export const AgentSettings = ({ style = {} }) => {
  const { selectedClient } = useContext(AppContext);
  const [agentOptions, setAgentOptions] = useState([]);
  const [workflowOptions, setWorkflowOptions] = useState([]);
  const { updatedEvent, setUpdatedEvent } = useLiveEvents();

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const agents = await getAgents(selectedClient.id);
        const options = agents.map((agent) => ({
          label: `${agent.name}`,
          value: agent.id,
        }));
        setAgentOptions(options);
      } catch (error) {
        console.error("Error fetching agents:", error);
      }
    };

    fetchAgents();

    const fetchWorkflows = async () => {
      try {
        const workflows = await getInferenceWorkflows(selectedClient.id);
        const options = workflows.filter(workflow => workflow.name.includes("Live Event")).map((workflow) => ({
          label: `${workflow.name}`,
          value: workflow.id,
        }));
        setWorkflowOptions(options);
      } catch (error) {
        console.error("Error fetching workflows:", error);
      }
    };

    fetchWorkflows();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <div style={{ display: "flex", flexDirection: "column", ...style }}>
      <FormElement>
        <FormLabel label="Moderator Agent Instructions" />
        <FormSubLabel label="Let the live event moderator agent know how to categorize questions correctly." />
        <FormInput
          as="textarea"
          rows={5}
          placeholder="Enter moderator agent instructions or notes here..."
          style={{ resize: "vertical" }}
          value={updatedEvent?.moderator_agent_instructions ?? ""}
          onChange={(e) => {
            updatedEvent.moderator_agent_instructions = e.target.value;
            setUpdatedEvent({ ...updatedEvent });
          }}
        />
      </FormElement>
      <FormElement>
        <FormLabel label="Supporting Documnentation" />
        <FormSubLabel label="Additional context or documentation provided to the live event agent to help answer participant questions." />
        <FormInput
          as="textarea"
          rows={5}
          placeholder="Enter supporiting documentation text"
          style={{ resize: "vertical" }}
          value={updatedEvent?.supporting_documentation ?? ""}
          onChange={(e) => {
            updatedEvent.supporting_documentation = e.target.value;
            setUpdatedEvent({ ...updatedEvent });
          }}
        />
      </FormElement>
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", marginBottom: "10px" }}>
        <FormElement style={{ width: "50%" }}>
          <FormLabel label="Live Agent" />
          <FormSubLabel label="The AI agent that will answer participant questions during the live event" />
          <select
            style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", fontSize: "16px" }}
            value={updatedEvent?.agent_id || ""}
            onChange={(e) => {
              updatedEvent.agent_id = e.target.value;
              setUpdatedEvent({ ...updatedEvent });
            }}
          >
            <option value="">Select an Agent</option>
            {agentOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </FormElement>
        <FormElement style={{ width: "50%", marginLeft: "20px" }}>
          <FormLabel label="Agent Workflow" />
          <FormSubLabel label="The inference workflow used by the live agent to answer questions" />
          <select
            style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", fontSize: "16px" }}
            value={updatedEvent?.agent_workflow_id || ""}
            onChange={(e) => {
              updatedEvent.agent_workflow_id = e.target.value;
              setUpdatedEvent({ ...updatedEvent });
            }}
          >
            <option value="">Select an Agent Workflow</option>
            {workflowOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </FormElement>
      </div>
      <FormElement>
        <FormLabel label="Agent Name" />
        <FormSubLabel label="The name of the agent that will respond to users." />
        <FormInput
          placeholder="Enter the name of the live event agent"
          value={updatedEvent?.agent_name ?? ""}
          onChange={(e) => {
            updatedEvent.agent_name = e.target.value;
            setUpdatedEvent({ ...updatedEvent });
          }}
        />
      </FormElement>
      <FormElement>
        <FormLabel label={`Relevance Threshold (${updatedEvent?.relevance_threshold ?? 0.5})`} />
        <FormSubLabel label="The minimum relevance score to display as a question" />
        <FormRange min="0" max="1" step="0.01"
          value={updatedEvent?.relevance_threshold ?? 0.5}
          onChange={(e) => {
            updatedEvent.relevance_threshold = parseFloat(e.target.value);
            setUpdatedEvent({ ...updatedEvent });
          }}
        />
      </FormElement>
      <FormElement>
        <FormLabel label={`Auto Moderation (${updatedEvent?.auto_moderation ? "On" : "Off"})`} />
        <FormSubLabel label="Allow the moderation agent to automatically prioritize questions for the hosts to answer, and label questions as toxic and/or off-topic" />
        <FormToggle
          checked={updatedEvent?.auto_moderation ?? false}
          onChange={(e) => {
            updatedEvent.auto_moderation = e.target.checked;
            setUpdatedEvent({ ...updatedEvent });
          }}
        />
      </FormElement>
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "20px" }}>
        <FormElement style={{ width: "500px" }}>
          <FormLabel label={`Real Time Answers (${updatedEvent?.realtime_answer ? "On" : "Off"})`} />
          <FormSubLabel label="Answer the users questions as they ask them if the answer is available, or wait until the end to show all users answers." />
          <FormToggle
            checked={updatedEvent?.realtime_answer ?? false}
            onChange={(e) => {
              updatedEvent.realtime_answer = e.target.checked;
              setUpdatedEvent({ ...updatedEvent });
            }}
          />
        </FormElement>
        {
          (updatedEvent?.event_type === "recorded" || updatedEvent?.event_type === "scheduled") && (
            <FormElement style={{ width: "500px" }}>
              <FormLabel label={`Use Pre-recorded Transcript (${updatedEvent?.use_recorded_transcript ? "On" : "Off"})`} />
              <FormSubLabel label="If selected, the answers for user questions will come from supporting documentation, as well as the entire transcript from the pre-recorded event." />
              <FormToggle
                checked={updatedEvent?.use_recorded_transcript ?? false}
                onChange={(e) => {
                  updatedEvent.use_recorded_transcript = e.target.checked;
                  setUpdatedEvent({ ...updatedEvent });
                }}
              />
            </FormElement>
          )
        }
      </div>
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "20px" }}>
        <FormElement style={{ width: "500px" }}>
          <FormLabel label={`Review Agent Answers (${updatedEvent?.review_agent_answers ? "On" : "Off"})`} />
          <FormSubLabel label="If on, the moderator will have to review and approve the agent's answer before it is shown to the user." />
          <FormToggle
            checked={updatedEvent?.review_agent_answers ?? false}
            onChange={(e) => {
              updatedEvent.review_agent_answers = e.target.checked;
              setUpdatedEvent({ ...updatedEvent });
            }}
          />
        </FormElement>
        <FormElement style={{ width: "500px" }}>
          <FormLabel label={`Real Time Answers to All  (${updatedEvent?.realtime_answers_to_all ? "On" : "Off"})`} />
          <FormSubLabel label="If on, the agent answers will be sent to all participants in their chat stream." />
          <FormToggle
            checked={updatedEvent?.realtime_answers_to_all ?? false}
            onChange={(e) => {
              updatedEvent.realtime_answers_to_all = e.target.checked;
              setUpdatedEvent({ ...updatedEvent });
            }}
          />
        </FormElement>
      </div>
    </div>
  );
};

export default AgentSettings;
